'use client'

import React, { type ReactNode, useId, useState } from 'react'
import { Info } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipPortal,
  TooltipTrigger,
  TooltipArrow,
} from '@/components/ui/tooltip'
import { cn } from '@/utilities/ui'

type TooltipPosition = 'top' | 'right' | 'bottom' | 'left'
type TooltipAlign = 'start' | 'center' | 'end'

interface InfoTooltipProps {
  title?: string
  content: ReactNode
  className?: string
  position?: TooltipPosition
  align?: TooltipAlign
  maxWidth?: string
  showExtraToolTipShadow?: boolean
  showToolTipArrow?: boolean
  contentClassName?: string
  trigger?: ReactNode
  /** Use an existing interactive element without adding a second button or changing its action. */
  triggerAsChild?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  triggerIconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  triggerIconProps?: React.SVGProps<SVGSVGElement>
  iconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  iconProps?: React.SVGProps<SVGSVGElement>
  triggerAriaLabel?: string
  triggerOnClick?: (e: React.MouseEvent) => void
  triggerClassName?: string
  delayDuration?: number
  /** When true, the tooltip stays closed (e.g. while a sibling popover is open). */
  forceClosed?: boolean
  /**
   * When true, a click toggles the tooltip and the tooltip stays open.
   * When false, hover opens the tooltip.
   */
  toggleOnClick?: boolean
  /** Single element used as the tooltip trigger via asChild. Do not nest another button. */
  children?: ReactNode
}

const INFO_TOOLTIP_TRIGGER_ATTR = 'data-info-tooltip-trigger' as const

type ClickToggleTriggerProps = {
  onClick?: (e: React.MouseEvent) => void
  onKeyDown?: (e: React.KeyboardEvent) => void
  [INFO_TOOLTIP_TRIGGER_ATTR]?: string
}

function resolveTriggerNode({
  children,
  trigger,
  triggerIconComponent,
  triggerIconProps,
  iconComponent,
  iconProps,
  className,
  triggerClassName,
  triggerAriaLabel,
  title,
  handleClick,
  handleTriggerKeyDown,
  triggerOnClick,
  toggleOnClick,
  triggerInstanceId,
}: {
  children?: ReactNode
  trigger?: ReactNode
  triggerIconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  triggerIconProps?: React.SVGProps<SVGSVGElement>
  iconComponent?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  iconProps?: React.SVGProps<SVGSVGElement>
  className: string
  triggerClassName: string
  triggerAriaLabel?: string
  title?: string
  handleClick: (e: React.MouseEvent) => void
  handleTriggerKeyDown: (e: React.KeyboardEvent) => void
  triggerOnClick?: (e: React.MouseEvent) => void
  toggleOnClick: boolean
  triggerInstanceId: string
}): React.ReactNode {
  const ariaLabel = triggerAriaLabel ?? (title ? `Information about ${title}` : 'Information')
  const triggerIdentityProps = { [INFO_TOOLTIP_TRIGGER_ATTR]: triggerInstanceId }

  if (children) {
    if (!toggleOnClick || !React.isValidElement(children)) {
      return children
    }

    const child = children as React.ReactElement<{
      onClick?: (event: React.MouseEvent) => void
    }>

    return React.cloneElement(child, {
      onClick: (event: React.MouseEvent) => {
        // preventDefault stops the tooltip close that runs on click.
        event.preventDefault()
        event.stopPropagation()
        child.props.onClick?.(event)
        handleClick(event)
      },
    })
  }

  if (trigger) {
    return (
      <div
        {...triggerIdentityProps}
        onClick={(e: React.MouseEvent) => {
          handleClick(e)
          triggerOnClick?.(e)
        }}
        onKeyDown={handleTriggerKeyDown}
        role="button"
        tabIndex={0}
        className={triggerClassName}
        aria-label={ariaLabel}
        data-testid="info-tooltip-trigger"
      >
        {trigger}
      </div>
    )
  }

  if (triggerIconComponent) {
    return (
      <button
        {...triggerIdentityProps}
        type="button"
        onClick={(e: React.MouseEvent) => {
          handleClick(e)
          triggerOnClick?.(e)
        }}
        className={cn('appearance-none bg-transparent border-0 p-0', triggerClassName)}
        aria-label={ariaLabel}
        data-testid="info-tooltip-trigger"
      >
        {React.createElement(triggerIconComponent, triggerIconProps)}
      </button>
    )
  }

  const IconComponent = iconComponent ?? Info

  return (
    <button
      {...triggerIdentityProps}
      type="button"
      className={cn(
        'inline-flex items-center justify-center active:text-tenant-primary-action-pressed-color',
        className,
      )}
      onClick={handleClick}
      aria-label={ariaLabel}
      data-testid="info-tooltip-trigger"
    >
      <IconComponent
        {...iconProps}
        className={cn(
          'h-4 w-4 text-tenant-description-text hover:text-dark-blue cursor-help',
          iconProps?.className,
        )}
        aria-hidden="true"
      />
    </button>
  )
}

export function InfoTooltip({
  title,
  content,
  className = '',
  position = 'bottom',
  align = 'center',
  maxWidth = '360px',
  showExtraToolTipShadow = false,
  showToolTipArrow = true,
  contentClassName = 'text-base text-placeholder leading-relaxed',
  trigger,
  triggerAsChild = false,
  open: controlledOpen,
  onOpenChange,
  triggerIconComponent,
  triggerIconProps,
  iconComponent,
  iconProps,
  triggerAriaLabel,
  triggerOnClick,
  triggerClassName = '',
  delayDuration = 0,
  forceClosed = false,
  toggleOnClick = false,
  children,
}: Readonly<InfoTooltipProps>) {
  const [internalOpen, setInternalOpen] = useState(false)
  const triggerInstanceId = useId()
  const open = controlledOpen ?? internalOpen
  const setOpen = (next: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(next)
    onOpenChange?.(next)
  }
  // Prefer maxTouchPoints / coarse pointer over `'ontouchstart' in window`.
  // jsdom and many desktop browsers expose ontouchstart without a touch screen.
  const [isTouchDevice] = useState(() => {
    const hasCoarsePointer = globalThis.matchMedia?.('(pointer: coarse)')?.matches ?? false
    return (
      'ontouchstart' in globalThis ||
      (globalThis.navigator?.maxTouchPoints ?? 0) > 0 ||
      hasCoarsePointer
    )
  })
  const clickToggles = toggleOnClick && controlledOpen === undefined
  const shouldUseRadixOpenChange = (triggerAsChild || isTouchDevice === false) && !clickToggles

  if ((forceClosed || (!clickToggles && children)) && internalOpen) {
    setInternalOpen(false)
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    if (!isTouchDevice && !clickToggles) return
    setOpen(!open)
  }

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    setOpen(!open)
    triggerOnClick?.(e as unknown as React.MouseEvent)
  }

  const tooltipOpenChange = forceClosed || !shouldUseRadixOpenChange ? undefined : setOpen
  const useControlledOpen =
    controlledOpen !== undefined || (isTouchDevice && !children) || clickToggles

  let triggerNode =
    trigger && triggerAsChild
      ? trigger
      : resolveTriggerNode({
          children,
          trigger,
          triggerIconComponent,
          triggerIconProps,
          iconComponent,
          iconProps,
          className,
          triggerClassName,
          triggerAriaLabel,
          title,
          handleClick,
          handleTriggerKeyDown,
          triggerOnClick,
          toggleOnClick: clickToggles,
          triggerInstanceId,
        })

  // Children are passed through asChild without InfoTooltip click wiring; attach toggle when needed.
  if (clickToggles && children && React.isValidElement(children)) {
    const child = children as React.ReactElement<ClickToggleTriggerProps>
    triggerNode = React.cloneElement(child, {
      [INFO_TOOLTIP_TRIGGER_ATTR]: triggerInstanceId,
      onClick: (e: React.MouseEvent) => {
        child.props.onClick?.(e)
        handleClick(e)
      },
      onKeyDown: (e: React.KeyboardEvent) => {
        child.props.onKeyDown?.(e)
        handleTriggerKeyDown(e)
      },
    })
  }

  return (
    <TooltipProvider>
      <Tooltip
        open={forceClosed ? false : useControlledOpen ? open : undefined}
        onOpenChange={
          forceClosed || !useControlledOpen
            ? undefined
            : clickToggles
              ? () => {
                  // Ignore hover/focus; click and keyboard handlers own open state.
                }
              : tooltipOpenChange
        }
        delayDuration={clickToggles ? 0 : delayDuration}
      >
        <TooltipTrigger asChild>{triggerNode}</TooltipTrigger>
        <TooltipPortal>
          <TooltipContent
            side={position}
            align={align}
            sideOffset={8}
            className={cn(
              'z-9999! p-3 bg-white rounded-2xl mx-4 sm:mx-0',
              showExtraToolTipShadow
                ? 'border-0 shadow-[0_0_16px_0_rgba(19,11,64,0.12)]'
                : 'border border-light-grey shadow-sm',
            )}
            style={{
              width: maxWidth,
              maxWidth: 'calc(100vw - 2rem)', // Prevent overflow with 1rem margin on each side
              zIndex: 9999,
            }}
            onPointerDownOutside={(event) => {
              // Trigger clicks are "outside" the content; let the click toggle handle those.
              const target = event.target as HTMLElement | null
              if (
                target?.closest(`[${INFO_TOOLTIP_TRIGGER_ATTR}="${CSS.escape(triggerInstanceId)}"]`)
              ) {
                return
              }
              setOpen(false)
            }}
            onEscapeKeyDown={() => setOpen(false)}
            data-testid="info-tooltip-content"
          >
            {showToolTipArrow && (
              <TooltipArrow
                className={cn(
                  'fill-white relative z-10000',
                  showExtraToolTipShadow
                    ? 'drop-shadow-[0_0_8px_rgba(19,11,64,0.14)]'
                    : 'drop-shadow-[0_1px_1px_rgba(19,11,64,0.08)]',
                )}
              />
            )}
            <div className={title ? 'space-y-2' : ''}>
              {title && <h4 className="font-medium text-sm text-dark-blue">{title}</h4>}
              {typeof content === 'function' ? (
                <div className={contentClassName}>{content}</div>
              ) : (
                <p className={contentClassName}>{content}</p>
              )}
            </div>
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  )
}
